import { memo, useCallback, useEffect, useState } from 'react'

import * as SC from './styles/SearchInputStyles'
import { useDebounce } from 'commons/hooks/common'
import { IconName, IconSize } from 'ui/components/Icon'

import { useEmpty } from './hooks'
import { BaseInputProps } from './props'
import { InputIconPosition, Value } from './types'

/**
 * This is a temp component for backward compatibility, use SearchInput with debounced request on the last mile.
 */
export const SearchInputDebounced = memo((props: BaseInputProps) => {
  const { value, onChange, disabled = false, ...rest } = props
  const [searchText, setSearchText] = useState<Value>(value ?? '')

  const debouncedOriginalOnChange = useDebounce(() => {
    if (onChange && searchText !== undefined) {
      onChange(searchText)
    }
  })

  const wrappedOnChange = useCallback(
    (value: string) => {
      setSearchText(value)
      debouncedOriginalOnChange()
    },
    [debouncedOriginalOnChange]
  )

  const { isEmpty, handleChange } = useEmpty(searchText, wrappedOnChange)

  const handleClear = useCallback(() => {
    setSearchText('')
    if (onChange) {
      onChange('')
    }
  }, [onChange])

  useEffect(() => {
    setSearchText(value)
  }, [value])

  return (
    <SC.BaseInputStyled
      {...rest}
      disabled={disabled}
      renderIcon={
        <SC.IconWrapper
          name={isEmpty ? IconName.SEARCH : IconName.CROSS_SMALL}
          size={IconSize.S}
          $notEmpty={!isEmpty}
          $disabled={disabled}
          onClick={handleClear}
        />
      }
      iconPosition={InputIconPosition.RIGHT_FAR}
      value={searchText}
      onChange={handleChange}
    />
  )
})
